import { AccountBalanceWalletOutlined, CheckCircleOutline, DescriptionOutlined, ScheduleOutlined, WarningAmberOutlined } from "@mui/icons-material";
import { Avatar, Box, Card, CardContent, Divider, Grid, LinearProgress, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { INegotiation, INegotiationBalance } from "../../../shared/services/api/sinister/AgreementRenegotiationService";
import { IAgreement } from "../../../shared/services/api/sinister/SinisterAgreementService";
import { SinisterService } from "../../../shared/services/api/sinister/SinisterService";
import { formatDateFnsRemoveTimezone } from "../../../shared/utils/format-date";
import { formatScaleValueBR } from "../../../shared/utils/formatScaleValue";
import { getPaymentMethodLabel } from "../../creditRecovery/constants/paymentMethodOptions";

interface Props {
	agreement: IAgreement;
	negotiation: INegotiation | null;
	balance: INegotiationBalance | null;
	isLoading: boolean;
}

interface BalanceCardConfig {
	key: string;
	label: string;
	subtitle: string;
	icon: JSX.Element;
	color: string;
	bg: string;
	value: number;
}

const buildBalanceCards = (balance: INegotiationBalance): BalanceCardConfig[] => [
	{
		key: "paid",
		label: "Pago",
		subtitle: "Valor já quitado",
		icon: <CheckCircleOutline fontSize="small" />,
		color: "#2fa887",
		bg: "#e9f8f5",
		value: balance.paidAmount,
	},
	{
		key: "open",
		label: "Em aberto",
		subtitle: "Parcelas a vencer",
		icon: <ScheduleOutlined fontSize="small" />,
		color: "#0f92d4",
		bg: "#e6f6fc",
		value: balance.openAmount,
	},
	{
		key: "overdue",
		label: "Vencido",
		subtitle: "Parcelas em atraso",
		icon: <WarningAmberOutlined fontSize="small" />,
		color: "#e0604c",
		bg: "#fdeeec",
		value: balance.overdueAmount,
	},
	{
		key: "toRecover",
		label: "A recuperar",
		subtitle: "Valor total do acordo",
		icon: <AccountBalanceWalletOutlined fontSize="small" />,
		color: "#8a4fd1",
		bg: "#f2ecfb",
		// Mapeamento direto do campo totalValue do balance — sem derivar/subtrair, igual aos outros três.
		value: balance.totalValue,
	},
];

// Anel percentual em CSS puro (conic-gradient) — mostra quanto do valor total do acordo aquele
// card representa, no mesmo espírito do "% usado" de dashboards de recursos (referência visual pedida).
const PercentRing = ({ percent, color, size = 44 }: { percent: number; color: string; size?: number }) => {
	const clamped = Math.min(Math.max(percent, 0), 100);
	return (
		<Box
			sx={{
				position: "relative",
				width: size,
				height: size,
				borderRadius: "50%",
				background: `conic-gradient(${color} ${clamped}%, rgba(0,0,0,0.08) ${clamped}% 100%)`,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				flexShrink: 0,
			}}
		>
			<Box
				sx={{
					width: size - 9,
					height: size - 9,
					borderRadius: "50%",
					bgcolor: "background.paper",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<Typography sx={{ fontSize: 11, fontWeight: 700, color }}>{Math.round(clamped)}%</Typography>
			</Box>
		</Box>
	);
};

const buildPaymentRows = (agreement: IAgreement): { label: string; value: string }[] => {
	const rows = [
		{ label: "Forma de pagamento", value: getPaymentMethodLabel(agreement.paymentMethod) },
		{
			label: "Desconto",
			value: agreement.discountPercent
				? `${agreement.discountPercent}% (${formatScaleValueBR(String(agreement.discountAmount))})`
				: "Nenhum",
		},
	];

	if (agreement.downPaymentValue) {
		rows.push({ label: "Entrada", value: formatScaleValueBR(String(agreement.downPaymentValue)) });
	}

	rows.push({ label: "Quantidade de parcelas", value: String(agreement.installmentsCount) });
	rows.push({ label: "Data do 1º pagamento", value: formatDateFnsRemoveTimezone(agreement.dueDate) || "-" });

	return rows;
};

// Não existe menu/tela separada de "Negociações" — negociação É o acordo. Os cards de saldo e os
// sinistros vinculados só aparecem quando o sinistro desse acordo já foi incluído numa negociação
// (fluxo de Renegociar/PR-06); acordos "simples" que nunca passaram por isso mostram só as condições
// de pagamento normais. Por isso essa seção sempre renderiza (nunca retorna null): Condições de
// Pagamento é informação de todo acordo, Negociação é um extra condicional dentro do mesmo card.
export const AgreementNegotiationSection = ({ agreement, negotiation, balance, isLoading }: Props) => {
	const hasNegotiation = !isLoading && !!negotiation;
	const balanceCards = hasNegotiation && balance ? buildBalanceCards(balance) : [];
	const paymentRows = buildPaymentRows(agreement);
	const [certificateBySinisterId, setCertificateBySinisterId] = useState<Record<string, string>>({});

	// Busca o número do certificado de cada sinistro vinculado — a negociação só devolve os IDs
	// (ver PR-06), então precisamos de uma chamada por sinistro pra exibir algo mais legível que o ID cru.
	useEffect(() => {
		const sinisterIds = negotiation?.sinisterIds ?? [];
		const pendingIds = sinisterIds.filter((id) => !(id in certificateBySinisterId));
		if (pendingIds.length === 0) return;

		Promise.all(
			pendingIds.map((id) =>
				SinisterService.getById(id).then((result) => [id, result instanceof Error ? "" : result.proposal?.certificateNumber ?? ""] as const)
			)
		).then((entries) => {
			setCertificateBySinisterId((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
		});
	}, [negotiation?.sinisterIds, certificateBySinisterId]);

	return (
		<Card variant="outlined" sx={{ borderRadius: 3, borderColor: "divider" }}>
			<CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
				<Typography variant="subtitle1" fontWeight={700} mb={2.5}>
					{hasNegotiation ? "Negociação e Condições de Pagamento" : "Condições de Pagamento"}
				</Typography>

				{balanceCards.length > 0 && (
					<Box mb={4}>
						<Grid container spacing={2}>
							{balanceCards.map((card) => {
								// Base do percentual precisa ser o totalValue do MESMO balance de onde vêm paid/open/overdue —
								// usar agreement.totalValue aqui misturava dois totais que nem sempre coincidem.
								const balanceTotal = balance?.totalValue ?? 0;
								const percent = balanceTotal > 0 ? (card.value / balanceTotal) * 100 : 0;
								return (
									<Grid item xs={12} sm={6} md={3} key={card.key}>
										<Box sx={{ p: 2.25, borderRadius: 3, bgcolor: card.bg, height: "100%" }}>
											<Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
												<Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
													<Avatar
														sx={{
															bgcolor: "background.paper",
															color: card.color,
															width: 42,
															height: 42,
															flexShrink: 0,
															boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
														}}
													>
														{card.icon}
													</Avatar>
													<Box minWidth={0}>
														<Typography variant="body2" fontWeight={700} noWrap>
															{card.label}
														</Typography>
														<Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
															{card.subtitle}
														</Typography>
													</Box>
												</Stack>
												<PercentRing percent={percent} color={card.color} />
											</Stack>

											<Typography variant="h6" fontWeight={800} sx={{ color: card.color }} noWrap>
												{formatScaleValueBR(String(card.value))}
											</Typography>

											<LinearProgress
												variant="determinate"
												value={Math.min(percent, 100)}
												sx={{
													mt: 1,
													height: 6,
													borderRadius: 3,
													bgcolor: "rgba(0,0,0,0.08)",
													"& .MuiLinearProgress-bar": { bgcolor: card.color, borderRadius: 3 },
												}}
											/>
										</Box>
									</Grid>
								);
							})}
						</Grid>
					</Box>
				)}

				<Box sx={{ bgcolor: "grey.50", borderRadius: 2, p: 2.5 }}>
					<Stack spacing={0.8}>
						{paymentRows.map((row, idx) => (
							<Box
								key={row.label}
								display="flex"
								justifyContent="space-between"
								py={0.3}
								sx={{ borderBottom: idx < paymentRows.length - 1 ? "1px dashed rgba(0,0,0,0.1)" : "none" }}
							>
								<Typography variant="body2" color="text.secondary">
									{row.label}
								</Typography>
								<Typography variant="body2" fontWeight={600}>
									{row.value}
								</Typography>
							</Box>
						))}
					</Stack>

					<Divider sx={{ my: 2 }} />

					<Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between", gap: 2 }}>
						<Stack>
							<Typography fontWeight={700}>💰 Valor total do acordo</Typography>
							<Typography fontWeight={600}>{formatScaleValueBR(String(agreement.totalValue))}</Typography>
						</Stack>

						<Stack>
							<Typography fontWeight={700}>💳 Parcelamento</Typography>
							<Typography fontWeight={600}>
								{agreement.installmentsCount}x via {getPaymentMethodLabel(agreement.paymentMethod)}
							</Typography>
						</Stack>
					</Box>
				</Box>

				{hasNegotiation && (
					<>
						<Divider sx={{ my: 3 }} />

						<Typography variant="body2" color="text.secondary" fontWeight={600} mb={1.5}>
							Sinistros vinculados ({negotiation!.sinisterIds.length})
						</Typography>
						<Grid container spacing={1.25}>
							{negotiation!.sinisterIds.map((sinisterId) => {
								const isLoaded = sinisterId in certificateBySinisterId;
								const certificateNumber = certificateBySinisterId[sinisterId];
								const primaryLabel = !isLoaded ? "Carregando..." : certificateNumber ? `Cert. ${certificateNumber}` : sinisterId;
								return (
									<Grid item xs={6} sm={4} md={3} key={sinisterId}>
										<Stack
											direction="row"
											spacing={1}
											alignItems="center"
											title={sinisterId}
											sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, px: 1.5, py: 1 }}
										>
											<DescriptionOutlined fontSize="small" color="action" />
											<Box minWidth={0}>
												<Typography variant="body2" fontWeight={500} noWrap>
													{primaryLabel}
												</Typography>
												{isLoaded && certificateNumber && (
													<Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
														{sinisterId}
													</Typography>
												)}
											</Box>
										</Stack>
									</Grid>
								);
							})}
						</Grid>
					</>
				)}
			</CardContent>
		</Card>
	);
};
